import type { PlayerApi, PlayerError, TimedMetadataEvent } from '@movingimage-evp/player-library';
import type { HTMLAttributes } from 'react';

import type { LsproPlayerConfig } from '../../use-player-config';

import { Player, PlayerEvent } from '@movingimage-evp/player-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type ECdnSettings, type HiveToken, ViewerSourceMode } from '../../../../../generated/graphql-viewer';
import { devLog } from '../../../../../utils';
import { useSelectedLanguage } from '../../../../providers/selected-language-provider';
import { usePlayerConfig } from '../../use-player-config';
import { UnmutePopup } from '../unmute-popup';

type ViewerPlayerProps = {
  webcastId: string;
  title?: string;
  accountId?: string;
  lsproId?: string;
  primaryColor?: string;
  iconColor?: string;
  logoUrl?: string;
  muted?: boolean;
  autoplay?: boolean;
  hidePlaybackSpeed?: boolean;
  employeeId?: string;
  primaryStreamReady?: boolean;
  backupStreamReady?: boolean;
  viewerSourceMode?: ViewerSourceMode;
  currentStream?: {
    playback: {
      primaryUrl: string;
      backupUrl?: string | null;
      token: string;
      ecdnSettings?: {
        hive?: {
          primary: HiveToken;
          backup?: HiveToken | null;
        } | null;
        vBrick?: ECdnSettings['vBrick'];
        kollective?: ECdnSettings['kollective'];
      } | null;
    };
  };
  onTimedMetadata?: (event: TimedMetadataEvent) => void;
  onPlayerRetry?: () => void;
  onOverlayVisibilityChange?: (visible: boolean) => void;
} & HTMLAttributes<HTMLDivElement>;

export function ViewerPlayer({
  webcastId,
  title,
  accountId,
  lsproId,
  primaryColor,
  iconColor,
  logoUrl,
  currentStream,
  muted = false,
  autoplay,
  hidePlaybackSpeed,
  employeeId,
  primaryStreamReady,
  backupStreamReady,
  viewerSourceMode,
  onTimedMetadata,
  onPlayerRetry,
  onOverlayVisibilityChange,
  ...props
}: ViewerPlayerProps) {
  const { t } = useTranslation();
  const errorTimeoutRef = useRef(0);
  const [unmutePopupVisible, setUnmutePopupVisible] = useState(false);
  const { selectedLanguage } = useSelectedLanguage();

  let streamToUse: 'primary' | 'backup' | null = null;

  if (viewerSourceMode === ViewerSourceMode.FORCE_PRIMARY) {
    streamToUse = 'primary';
  } else if (viewerSourceMode === ViewerSourceMode.FORCE_BACKUP) {
    streamToUse = 'backup';
  } else if (viewerSourceMode === ViewerSourceMode.AUTO) {
    if (primaryStreamReady) {
      streamToUse = 'primary';
    } else if (backupStreamReady) {
      streamToUse = 'backup';
    }
  }

  const primaryPlaybackUrl = currentStream?.playback.primaryUrl || '';
  const backupPlaybackUrl = currentStream?.playback.backupUrl || '';
  const playbackTokenRef = useRef(currentStream?.playback.token);

  const playbackBaseUrl =
    streamToUse === 'primary' ? primaryPlaybackUrl : streamToUse === 'backup' ? backupPlaybackUrl : '';
  const token = playbackTokenRef.current ? `?hdnts=${playbackTokenRef.current}` : '';
  const playbackUrl = playbackBaseUrl + token;

  const vBrickConfiguration = currentStream?.playback.ecdnSettings?.vBrick;
  const kollectiveConfigurationRef = useRef(currentStream?.playback.ecdnSettings?.kollective);

  // Refresh values if the stream type changes
  useEffect(() => {
    if (currentStream?.playback.token) playbackTokenRef.current = currentStream.playback.token;
    if (currentStream?.playback.ecdnSettings?.kollective)
      kollectiveConfigurationRef.current = currentStream.playback.ecdnSettings?.kollective;
  }, [currentStream?.playback.ecdnSettings?.kollective, currentStream?.playback.token, streamToUse]);

  const primaryHiveConfig = currentStream?.playback.ecdnSettings?.hive?.primary;
  const backupHiveConfig = currentStream?.playback.ecdnSettings?.hive?.backup;
  const hiveConfiguration =
    streamToUse === 'primary' ? primaryHiveConfig : streamToUse === 'backup' ? backupHiveConfig : null;

  const lsproPlayerConfig = useMemo(
    (): LsproPlayerConfig => ({
      webcastId,
      contentId: webcastId,
      playbackUrl,
      title,
      accountId,
      lsproId,
      primaryColor,
      iconColor,
      logoUrl,
      muted,
      autoplay,
      hidePlaybackSpeed,
      hiveConfiguration,
      vBrickConfiguration,
      kollectiveConfiguration: kollectiveConfigurationRef.current,
      state: 'Live',
      language: selectedLanguage?.code,
      watermarkContent: employeeId,
    }),
    [
      accountId,
      autoplay,
      hidePlaybackSpeed,
      hiveConfiguration,
      logoUrl,
      lsproId,
      muted,
      playbackUrl,
      primaryColor,
      iconColor,
      selectedLanguage,
      title,
      vBrickConfiguration,
      webcastId,
      employeeId,
    ]
  );

  const playerConfig = usePlayerConfig(lsproPlayerConfig);
  const playerApiRef = useRef<PlayerApi | null>(null);

  const handleVolumeChange = useCallback(() => {
    if (!playerApiRef.current?.getMuted()) {
      setUnmutePopupVisible(false);
    }
  }, []);

  const playVideo = useCallback(
    async (api: PlayerApi) => {
      try {
        if (api.getPaused()) await api.play();
      } catch {
        try {
          api.setMuted(true);
          setUnmutePopupVisible(true);
          api.addEventListener(PlayerEvent.VOLUME_CHANGE, handleVolumeChange);

          await api.play();
        } catch {
          return;
        }
      }
    },
    [handleVolumeChange]
  );

  useEffect(() => {
    if (!unmutePopupVisible) {
      playerApiRef.current?.removeEventListener(PlayerEvent.VOLUME_CHANGE, handleVolumeChange);
    }
  }, [handleVolumeChange, unmutePopupVisible]);

  const playerReadyHandler = useCallback(
    (api: PlayerApi) => {
      const handlePlayerError = (playerError: PlayerError) => {
        devLog('⚠️ ViewerPlayer error', playerError);
        setUnmutePopupVisible(false);

        // Only call onPlayerRetry if it hasn't been called in the last 5 seconds
        if (Date.now() - errorTimeoutRef.current > 5000) {
          onPlayerRetry?.();
          errorTimeoutRef.current = Date.now();
        }
      };

      // Player ready event can be called multiple times,
      // so we need to make sure we only add the event listeners once
      if (playerApiRef.current !== api) {
        if (onTimedMetadata) playerApiRef.current?.removeEventListener(PlayerEvent.TIMED_METADATA, onTimedMetadata);
        playerApiRef.current?.removeEventListener(PlayerEvent.ERROR, handlePlayerError);
      }

      if (onTimedMetadata) api.addEventListener(PlayerEvent.TIMED_METADATA, onTimedMetadata);
      api.addEventListener(PlayerEvent.ERROR, handlePlayerError);
      devLog('✅ ViewerPlayer ready');
      playerApiRef.current = api;

      if (autoplay) playVideo(api);
    },
    [autoplay, playVideo, onTimedMetadata, onPlayerRetry]
  );

  const unmuteVideo = () => {
    setUnmutePopupVisible(false);
    playerApiRef.current?.setMuted(false);
  };

  useEffect(() => {
    if (playbackBaseUrl && playerConfig) devLog('⏯️ ViewerPlayer playerConfig', playerConfig, { memoize: true });
  }, [playbackBaseUrl, playerConfig]);

  if (!playbackBaseUrl || !playerConfig) return null;

  return (
    <div data-testid={streamToUse === 'primary' ? 'primary-stream-player' : 'backup-stream-player'} {...props}>
      <Player {...playerConfig} onReady={playerReadyHandler} onOverlayVisibilityChange={onOverlayVisibilityChange} />

      {unmutePopupVisible && (
        <UnmutePopup
          title={t('viewer.views.unmutePopup.title.eventStarted')}
          onClose={() => setUnmutePopupVisible(false)}
          onUnMute={unmuteVideo}
        />
      )}
    </div>
  );
}
